import { Text, TextProps, Tooltip, NativeAttributes, TooltipProps } from 'pounceV2';
import React from 'react';

export interface OverflowTextWithTooltipProps extends TextProps {
  numLines?: number;
  placement?: TooltipProps['alignment'];
  disableTooltip?: boolean;
  customTooltip?: string | React.ReactElement;
}

const OverflowTextWithTooltipV3 = ({
  children,
  numLines = 1,
  placement = 'bottom-center',
  disableTooltip = false,
  customTooltip,
  ...textProps
}: OverflowTextWithTooltipProps) => {
  const [isOverflowed, setIsOverflow] = React.useState(false);
  const textElementRef = React.useRef<HTMLParagraphElement>();

  // Checks if text has overflowed to know if the tooltip with the full title should be shown
  React.useEffect(() => {
    if (!textElementRef.current) {
      setIsOverflow(false);
      return;
    }

    const element = textElementRef.current;
    // Add tolerance for browser rounding differences (especially Firefox)
    // Firefox sometimes reports scrollWidth as 1-2px larger due to sub-pixel rendering
    const TOLERANCE = 2;

    const hasHorizontalOverflow = element.scrollWidth - element.offsetWidth > TOLERANCE;
    const hasVerticalOverflow = element.scrollHeight - element.offsetHeight > TOLERANCE;

    setIsOverflow(hasHorizontalOverflow || hasVerticalOverflow);
  }, [children, numLines, customTooltip]);

  const overflowStyles = {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    wordWrap: 'break-word',
    // This prevents long, unbroken strings from preventing the text from properly wrapping.
    wordBreak: 'break-all',
    style: {
      maxWidth: '100%',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: numLines,
    },
  } as NativeAttributes<'p'>;

  const text = children as string;

  return isOverflowed && !disableTooltip ? (
    <Tooltip content={customTooltip || text} alignment={placement}>
      <Text ref={textElementRef} {...overflowStyles} {...textProps}>
        {text}
      </Text>
    </Tooltip>
  ) : (
    <Text ref={textElementRef} {...overflowStyles} {...textProps}>
      {text}
    </Text>
  );
};

export default OverflowTextWithTooltipV3;
